/**
 * GTM & dataLayer utility for storefront analytics.
 * Pushes GA4-standard ecommerce events so merchants can track
 * via Google Tag Manager without any additional GA4 script.
 */

// ── GTM Script Injection ──

let gtmLoaded = null; // track which container is loaded

export function loadGTM(containerId) {
  if (!containerId || gtmLoaded === containerId) return;
  gtmLoaded = containerId;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  // GTM head script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);

  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // GTM noscript iframe (for non-JS crawlers)
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export function unloadGTM() {
  gtmLoaded = null;
}

// ── dataLayer Push Helpers ──

function push(event, data = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce
  window.dataLayer.push({ event, ...data });
}

function mapItem(product, currency, index = 0, listName = '') {
  return {
    item_id: product.id || product.slug || '',
    item_name: product.name || '',
    price: Number(product.sale_price || product.base_price || product.price || 0),
    currency: currency || 'EUR',
    index,
    ...(product.category_name ? { item_category: product.category_name } : {}),
    ...(listName ? { item_list_name: listName } : {}),
    quantity: product.quantity || 1,
  };
}

// ── GA4 Ecommerce Events ──

export function trackPageView(pagePath, pageTitle) {
  push('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

export function trackViewItemList(products, currency, listName = 'Store') {
  push('view_item_list', {
    ecommerce: {
      item_list_name: listName,
      items: products.slice(0, 20).map((p, i) => mapItem(p, currency, i, listName)),
    },
  });
}

export function trackViewItem(product, currency) {
  push('view_item', {
    ecommerce: {
      currency: currency || 'EUR',
      value: Number(product.sale_price || product.base_price || 0),
      items: [mapItem(product, currency)],
    },
  });
}

export function trackAddToCart(product, currency, quantity = 1) {
  const price = Number(product.sale_price || product.base_price || product.price || 0);
  push('add_to_cart', {
    ecommerce: {
      currency: currency || 'EUR',
      value: price * quantity,
      items: [{ ...mapItem(product, currency), quantity }],
    },
  });
}

export function trackRemoveFromCart(product, currency, quantity = 1) {
  const price = Number(product.sale_price || product.base_price || product.price || 0);
  push('remove_from_cart', {
    ecommerce: {
      currency: currency || 'EUR',
      value: price * quantity,
      items: [{ ...mapItem(product, currency), quantity }],
    },
  });
}

export function trackBeginCheckout(cartItems, currency, totalValue) {
  const items = Array.isArray(cartItems) ? cartItems : [];
  push('begin_checkout', {
    ecommerce: {
      currency: currency || 'EUR',
      value: Number(totalValue || 0),
      items: items.map((item, i) => mapItem(item, currency, i)),
    },
  });
}

export function trackAddShippingInfo(cartItems, currency, totalValue, shippingTier) {
  const items = Array.isArray(cartItems) ? cartItems : [];
  push('add_shipping_info', {
    ecommerce: {
      currency: currency || 'EUR',
      value: Number(totalValue || 0),
      shipping_tier: shippingTier || 'standard',
      items: items.map((item, i) => mapItem(item, currency, i)),
    },
  });
}

export function trackAddPaymentInfo(cartItems, currency, totalValue, paymentMethod) {
  const items = Array.isArray(cartItems) ? cartItems : [];
  push('add_payment_info', {
    ecommerce: {
      currency: currency || 'EUR',
      value: Number(totalValue || 0),
      payment_type: paymentMethod || 'unknown',
      items: items.map((item, i) => mapItem(item, currency, i)),
    },
  });
}

export function trackPurchase(order, currency) {
  const items = (order.items || []).map((item, i) => ({
    item_id: item.product_id || item.id || '',
    item_name: item.name || '',
    price: Number(item.unit_price || item.price || 0),
    currency: currency || 'EUR',
    quantity: item.quantity || 1,
    index: i,
  }));

  push('purchase', {
    ecommerce: {
      transaction_id: order.order_number || order.id || '',
      currency: currency || 'EUR',
      value: Number(order.total || 0),
      shipping: Number(order.delivery_fee || 0),
      tax: 0,
      items,
      ...(order.discount_code ? { coupon: order.discount_code } : {}),
    },
  });
}
